import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Crown,
  Shield,
  User,
  Eye,
  Trash2,
  Plus,
  Loader2,
  AlertCircle,
  ChevronDown,
  Users,
} from 'lucide-react';
import apiClient from '../../lib/api';

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin', icon: Shield, description: 'Can manage team and members' },
  { value: 'member', label: 'Member', icon: User, description: 'Can view and edit data' },
  { value: 'viewer', label: 'Viewer', icon: Eye, description: 'Read-only access' },
];

const getRoleIcon = (role) => {
  switch (role) {
    case 'owner': return Crown;
    case 'admin': return Shield;
    case 'member': return User;
    case 'viewer': return Eye;
    default: return User;
  }
};

const getRoleBadgeColor = (role) => {
  switch (role) {
    case 'owner': return 'bg-purple-100 text-purple-700';
    case 'admin': return 'bg-blue-100 text-blue-700';
    case 'member': return 'bg-green-100 text-green-700';
    case 'viewer': return 'bg-gray-100 text-gray-600';
    default: return 'bg-gray-100 text-gray-600';
  }
};

const UserTeamsModal = ({ isOpen, onClose, user, allTeams, userTeams, onTeamsChanged }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [selectedRole, setSelectedRole] = useState('member');
  const [editingMembershipId, setEditingMembershipId] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  // Get teams this user is not yet a member of
  const availableTeams = allTeams.filter(
    team => !userTeams.some(ut => ut.teamId === team.id)
  );

  const handleAddToTeam = async () => {
    if (!selectedTeamId || !user) return;

    setActionLoading('add');
    setError(null);

    try {
      await apiClient.addTeamMember(selectedTeamId, user.id, selectedRole);
      setShowAddTeam(false);
      setSelectedTeamId('');
      setSelectedRole('member');
      onTeamsChanged();
    } catch (err) {
      console.error('Error adding user to team:', err);
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateRole = async (membership, newRole) => {
    if (!membership || membership.role === 'owner') return;

    setActionLoading(membership.id);
    setError(null);

    try {
      await apiClient.updateTeamMember(membership.teamId, membership.id, newRole);
      setEditingMembershipId(null);
      onTeamsChanged();
    } catch (err) {
      console.error('Error updating role:', err);
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemoveFromTeam = async (membership) => {
    if (!membership || membership.role === 'owner') return;

    if (!confirm(`Remove ${user?.name || user?.email} from ${membership.teamName}?`)) {
      return;
    }

    setActionLoading(membership.id);
    setError(null);

    try {
      await apiClient.removeTeamMember(membership.teamId, membership.id);
      onTeamsChanged();
    } catch (err) {
      console.error('Error removing from team:', err);
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black bg-opacity-25"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative bg-white rounded-xl shadow-xl max-w-lg w-full p-6"
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Team Memberships</h3>
                  <p className="text-sm text-gray-500">{user?.name || user?.email}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Error */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-red-700">{error}</p>
                  <button
                    onClick={() => setError(null)}
                    className="text-xs text-red-600 hover:text-red-800 mt-1"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}

            {/* Current Teams */}
            <div className="space-y-2 mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Current Teams</h4>

              {userTeams.length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center">
                  Not a member of any team
                </p>
              ) : (
                <div className="space-y-2 max-h-[240px] overflow-y-auto">
                  {userTeams.map((membership) => {
                    const RoleIcon = getRoleIcon(membership.role);
                    const isOwner = membership.role === 'owner';
                    const isLoading = actionLoading === membership.id;

                    return (
                      <div
                        key={membership.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">
                            {membership.teamName}
                          </span>
                        </div>

                        <div className="flex items-center gap-2">
                          {editingMembershipId === membership.id && !isOwner ? (
                            <select
                              value={membership.role}
                              onChange={(e) => handleUpdateRole(membership, e.target.value)}
                              onBlur={() => setEditingMembershipId(null)}
                              disabled={isLoading}
                              className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              autoFocus
                            >
                              {ROLE_OPTIONS.map((role) => (
                                <option key={role.value} value={role.value}>
                                  {role.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <button
                              onClick={() => !isOwner && setEditingMembershipId(membership.id)}
                              disabled={isOwner || isLoading}
                              className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${getRoleBadgeColor(membership.role)} ${
                                !isOwner ? 'cursor-pointer hover:opacity-80' : 'cursor-default'
                              }`}
                              title={isOwner ? 'Cannot change owner role' : 'Click to change role'}
                            >
                              {isLoading ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <RoleIcon className="w-3 h-3" />
                              )}
                              <span className="capitalize">{membership.role}</span>
                              {!isOwner && <ChevronDown className="w-3 h-3" />}
                            </button>
                          )}

                          {!isOwner && (
                            <button
                              onClick={() => handleRemoveFromTeam(membership)}
                              disabled={isLoading}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                              title="Remove from team"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Add to Team */}
            {availableTeams.length > 0 && (
              <div className="border-t border-gray-200 pt-4">
                {showAddTeam ? (
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-gray-700">Add to Team</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Team</label>
                        <select
                          value={selectedTeamId}
                          onChange={(e) => setSelectedTeamId(e.target.value)}
                          disabled={actionLoading === 'add'}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Select team...</option>
                          {availableTeams.map((team) => (
                            <option key={team.id} value={team.id}>
                              {team.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Role</label>
                        <select
                          value={selectedRole}
                          onChange={(e) => setSelectedRole(e.target.value)}
                          disabled={actionLoading === 'add'}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          {ROLE_OPTIONS.map((role) => (
                            <option key={role.value} value={role.value}>
                              {role.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setShowAddTeam(false);
                          setSelectedTeamId('');
                          setSelectedRole('member');
                        }}
                        disabled={actionLoading === 'add'}
                        className="flex-1 px-3 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleAddToTeam}
                        disabled={!selectedTeamId || actionLoading === 'add'}
                        className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {actionLoading === 'add' ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Plus className="w-4 h-4" />
                        )}
                        Add
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowAddTeam(true)}
                    className="w-full px-3 py-2 text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 text-sm flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add to Team
                  </button>
                )}
              </div>
            )}

            {/* Close Button */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
};

export default UserTeamsModal;
